import type {AutocompleteItem} from '@mantine/core';
import {useEffect, useMemo, useState} from 'react';
import {
  getDepartmentsInSummary,
  useGetCompaniesInSummary,
} from '../../../core/repositories/reportSummaryRepository';

type Props = {
  reportType: string;
};

type DepartmentItem = {
  id: number;
  name: string;
};

const DEFAULT_DEPARTMENT_ID = '0';

export const useHandleReportAnalyticsSearch = ({reportType}: Props) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [searchWord, setSearchWord] = useState('');
  const [departmentItems, setDepartmentItems] = useState<DepartmentItem[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | undefined
  >(undefined);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(
    DEFAULT_DEPARTMENT_ID,
  );

  useEffect(() => {
    if (!selectedCompanyId) return;

    void getDepartmentsInSummary({
      companyID: Number.parseInt(selectedCompanyId, 10),
      queryParams: {reportType},
    })
      .then((result) => {
        if (result.ok && result.value.departments) {
          const departments = result.value.departments.map((department) => ({
            id: department.departmentID,
            name: department.departmentName,
          }));
          setDepartmentItems(departments);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, [selectedCompanyId, reportType]);

  const {data} = useGetCompaniesInSummary({queryParams: {reportType}});

  const handleSearchInputChange = (searchWord: string) => {
    setSearchWord(searchWord);
  };

  const autoCompleteData: AutocompleteItem[] = useMemo(
    () =>
      data?.ok
        ? data.value.companies.map((company) => ({
            id: company.companyID,
            value: company.companyName,
          }))
        : [],
    [data],
  );

  const convertDepartmentItems = useMemo(() => {
    const defaultOption = {
      value: DEFAULT_DEPARTMENT_ID,
      label: '--部門・職種を選択してください--',
    };

    const departmentOptions = departmentItems.map((item) => ({
      value: String(item.id),
      label: item.name,
    }));

    return [defaultOption, ...departmentOptions];
  }, [departmentItems]);

  return {
    searchWord,
    autoCompleteData,
    handleSearchInputChange,
    convertDepartmentItems,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedDepartmentId,
    setSelectedDepartmentId,
    setDepartmentItems,
  };
};
